import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { SignupForm } from '@/components/auth/SignupForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, Home, Languages } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

export function SignupPage() {
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const { t, dir, language, setLanguage } = useLanguage();
  const navigate = useNavigate();

  const toggleLanguage = () => setLanguage(language === 'en' ? 'ar' : 'en');

  const TopBar = () => (
    <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
      <button
        onClick={() => navigate('/landing')}
        className="text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-white/60 flex items-center gap-1.5 text-sm font-medium"
      >
        <Home className="h-4 w-4" />
        {t('auth.backToHomepage') || 'العودة للرئيسية'}
      </button>
      <button
        onClick={toggleLanguage}
        className="text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-white/60 flex items-center gap-1.5 text-sm font-medium"
      >
        <Languages className="h-4 w-4" />
        {language === 'en' ? 'العربية' : 'English'}
      </button>
    </div>
  );

  useEffect(() => {
    checkSignupStatus();
  }, []);

  const checkSignupStatus = async () => {
    try {
      setLoading(true);
      const settingsDoc = await getDoc(doc(db, 'settings', 'globalSignup'));
      
      if (settingsDoc.exists()) {
        setSignupEnabled(settingsDoc.data().enabled !== false);
      } else {
        // Default to enabled if no setting exists
        setSignupEnabled(true);
      }
    } catch (error) {
      console.error('Error checking signup status:', error);
      // Default to enabled on error
      setSignupEnabled(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-secondary/30 px-4 py-8">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center gap-2">
            <img 
              src="/namatplatformlogo.png"
              alt="Namat Platform"
              className="h-auto w-[250px] object-contain" 
            />
          </div>
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (!signupEnabled) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-secondary/30 px-4 py-8">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center gap-2">
            <img 
              src="/namatplatformlogo.png"
              alt="Namat Platform"
              className="h-auto w-[250px] object-contain" 
            />
          </div>
          <Card>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              <CardTitle className="text-xl font-semibold text-red-700 dark:text-red-300">
                {t('auth.signupDisabled.title') || 'Signup Temporarily Disabled'}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                {t('auth.signupDisabled.message') || 
                 'Public signup is currently disabled. Please contact an administrator to create an account for you.'}
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/login')}
                className="w-full"
              >
                {t('auth.signupDisabled.backToLogin') || 'Back to Login'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-secondary/30 px-4 py-8 relative" dir={dir}>
      <TopBar />
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-2">
          <img 
            src="/namatplatformlogo.png"
            alt="Namat Platform"
            className="h-auto w-[250px] object-contain" 
          />
        </div>
        <SignupForm />
      </div>
    </div>
  );
}